import { QueryClient, useMutation, useQueryClient } from 'react-query';

import { ModelView, UpdateModelView } from '@mpm-druid/types';
import api from '@shared/api';

async function updateModelView({
  modelId,
  partialView,
  viewId
}: {
  modelId: string;
  partialView: UpdateModelView;
  viewId: string;
}) {
  const { data } = await api.patch<ModelView>(
    `mpm/models/${modelId}/views/${viewId}`,
    {
      data: partialView.data,
      name: partialView.name,
      version: partialView.version
    }
  );

  return data;
}

export function runUpdateModelView(
  queryClient: QueryClient,
  variables: { modelId: string; partialView: UpdateModelView; viewId: string }
) {
  return queryClient
    .getMutationCache()
    .build(queryClient, {
      mutationKey: [
        'model-view-upsert',
        { modelId: variables.modelId, viewId: variables.viewId }
      ],
      mutationFn: () => updateModelView(variables),
      onSuccess: async (updatedView, { modelId }) => {
        queryClient.setQueryData(
          ['model-view', { modelId, viewId: updatedView.id }],
          updatedView
        );
        await queryClient.invalidateQueries([
          'model-summary-views',
          { modelId }
        ]);
      },
      variables
    })
    .execute();
}

export function useModelViewUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      modelId,
      partialView,
      viewId
    }: {
      modelId: string;
      partialView: UpdateModelView;
      viewId: string;
    }) => {
      return updateModelView({ modelId, partialView, viewId });
    },
    {
      onSuccess: async (updatedView, { modelId }) => {
        queryClient.setQueryData(
          ['model-view', { modelId, viewId: updatedView.id }],
          updatedView
        );
        await queryClient.invalidateQueries([
          'model-summary-views',
          { modelId }
        ]);
      }
    }
  );
}
